import { useEffect, useRef } from "react";
import { API_BASE } from "@/lib/api";

/**
 * Subscribes to the backend SSE stream (GET /api/events) and invokes `onEvent`
 * for every event received. Auto-reconnects with backoff. The connection is
 * only opened while a token is present in localStorage (i.e. logged in).
 *
 * Events are small typed signals: { type, ts, lead_id?, ... }. Callers should
 * use them to trigger a refetch of the affected data, not to read data from.
 *
 * @param {(event: object) => void} onEvent
 */
export function useLiveEvents(onEvent) {
  const handlerRef = useRef(onEvent);
  handlerRef.current = onEvent;

  useEffect(() => {
    let es = null;
    let retry = 0;
    let reconnectTimer = null;
    let closed = false;

    const connect = () => {
      const token = localStorage.getItem("dermatrix_token");
      if (!token) {
        // Not logged in yet — try again shortly.
        reconnectTimer = setTimeout(connect, 3000);
        return;
      }

      // EventSource can't send headers, so the token goes in the query string.
      es = new EventSource(`${API_BASE}/events?token=${encodeURIComponent(token)}`);

      es.onopen = () => {
        retry = 0;
      };

      es.onmessage = (e) => {
        if (!e.data) return;
        try {
          const event = JSON.parse(e.data);
          handlerRef.current?.(event);
        } catch {
          // ignore keepalives / malformed lines
        }
      };

      es.onerror = () => {
        // Browser will retry on its own for transient drops, but if the server
        // closed (e.g. token expired) we reconnect manually with backoff.
        es?.close();
        es = null;
        if (closed) return;
        retry = Math.min(retry + 1, 6);
        const delay = Math.min(1000 * 2 ** retry, 30000);
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      es?.close();
    };
  }, []);
}
